import React, { useContext, useEffect, useState, useCallback, useRef } from 'react';
import type { OMSConfig, OMSOrder, StrategyContext } from './types';
import { OMSContext, type OMSContextType } from './OMSContext';
import { getFirebaseAuth, getFirestore } from '../../services/firebase';
import { doc, onSnapshot, setDoc } from 'firebase/firestore';
import { buildProcessedHistory, calculateTrendAnalysis, cellDataKeys, getRowMin, ensureFlatOpts } from '../analysis/trend-evaluator';
import { useMarketStore } from '../../services/store';
import { useSettingsStore } from '../../stores/settingsStore';
import { extractSpotPrice, extractSnapshotOptions, find0916Snapshot, extractTimePart } from '../../lib/snapshot-search';
import { strategyEvaluators } from './strategies';
import { getSupportResistance, getConnectingDotsRows, getSortKey } from './strategies/shared';
import { STRIKE_STEP } from '../../config/constants';
import { fetchLotSizeFromCSV } from '../../lib/history-utils';

export { OMSContext };

export const OMSProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [configs, setConfigs] = useState<Record<string, OMSConfig>>({});
    const [orders, setOrders] = useState<OMSOrder[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    
    // Prevent infinite loops when syncing from cloud
    const isUpdatingFromCloud = useRef(false);

    const loadData = useCallback(async () => {
        setIsLoading(true);
        try {
            // Load configs from memory (localStorage) as fallback/fast-boot
            try {
                const localConfigs = localStorage.getItem('oms_configs_v1');
                if (localConfigs) {
                    setConfigs(JSON.parse(localConfigs));
                }
            } catch (e) {
                console.error("Local storage error", e);
            }

            // Real-time Cloud Sync setup
            const auth = getFirebaseAuth();
            const db = getFirestore();
            
            // Wait a moment for auth state to initialize if it hasn't
            await new Promise(resolve => setTimeout(resolve, 500));
            
            const currentUser = auth.currentUser;
            if (currentUser) {
                const docRef = doc(db, 'user_oms_configs', currentUser.uid);
                
                // Subscribe to real-time changes from other devices
                onSnapshot(docRef, (docSnap) => {
                    if (docSnap.exists()) {
                        const cloudConfigs = docSnap.data() as Record<string, OMSConfig>;
                        isUpdatingFromCloud.current = true;
                        setConfigs(prev => {
                            const merged = { ...prev, ...cloudConfigs };
                            try { localStorage.setItem('oms_configs_v1', JSON.stringify(merged)); } catch (e) { console.warn('Ignored error in OMSProvider.tsx:', e); }
                            return merged;
                        });
                        setTimeout(() => { isUpdatingFromCloud.current = false; }, 100);
                    } else {
                        // Document doesn't exist, so upload local configurations to cloud!
                        const localData = localStorage.getItem('oms_configs_v1');
                        if (localData) {
                            setDoc(docRef, JSON.parse(localData), { merge: true }).catch(err => console.error("[AsyncCatch] Operation failed:", err));
                        }
                    }
                });
            }

            // Attempt to load mock orders if any
            try {
                const token = await auth.currentUser?.getIdToken();
                if (token) {
                    const ordersRes = await fetch('/api/oms/orders', {
                        headers: { Authorization: `Bearer ${token}` }
                    }).then(r => r.json()).catch(() => null);
                    if (ordersRes && ordersRes.success && ordersRes.orders) {
                        setOrders(ordersRes.orders);
                    }
                }
            } catch (e) { console.warn('Ignored error in OMSProvider.tsx:', e); }
            
        } catch (error) {
            console.error('Failed to load OMS data:', error);
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const updateConfig = async (config: OMSConfig) => {
        setConfigs(prev => {
            const next = { ...prev, [config.strategy_id]: config };
            try {
                localStorage.setItem('oms_configs_v1', JSON.stringify(next));
            } catch (e) { console.warn('Ignored error in OMSProvider.tsx:', e); }
            return next;
        });
        
        // Push to cloud if logged in
        if (!isUpdatingFromCloud.current) {
            try {
                const auth = getFirebaseAuth();
                const db = getFirestore();
                if (auth.currentUser) {
                    const docRef = doc(db, 'user_oms_configs', auth.currentUser.uid);
                    await setDoc(docRef, { [config.strategy_id]: config }, { merge: true });
                }
            } catch (error) {
                console.error('Failed to sync config to cloud:', error);
            }
        }
    };

    const addOrder = async (order: OMSOrder) => {
        setOrders(prev => [order, ...prev]);
        try {
            const token = await getFirebaseAuth().currentUser?.getIdToken();
            await fetch('/api/oms/orders', {
                method: 'POST',
                headers: { 
                    'Content-Type': 'application/json',
                    ...(token ? { Authorization: `Bearer ${token}` } : {})
                },
                body: JSON.stringify(order)
            });
        } catch (error) {
            console.error('Failed to add order:', error);
        }
    };

    const updateOrder = async (order: OMSOrder) => {
        setOrders(prev => prev.map(o => o.id === order.id ? order : o));
        try {
            const token = await getFirebaseAuth().currentUser?.getIdToken();
            await fetch('/api/oms/orders/update', {
                method: 'POST',
                headers: { 
                    'Content-Type': 'application/json',
                    ...(token ? { Authorization: `Bearer ${token}` } : {})
                },
                body: JSON.stringify(order)
            });
        } catch (error) {
            console.error('Failed to update order:', error);
        }
    };

    const addOrderLocal = useCallback((order: OMSOrder) => {
        setOrders(prev => [order, ...prev]);
    }, []);

    const updateOrderLocal = useCallback((order: OMSOrder) => {
        setOrders(prev => prev.map(o => o.id === order.id ? order : o));
    }, []);

    const runBacktest = useCallback(async (symbol: string, expiry: string) => {
        setIsLoading(true);
        try {
            // Emulate local fast backtest
            // Imports are now statically handled at the top level
            
            
            const state = useMarketStore.getState();
            const inspectionSource = useSettingsStore.getState().inspectionSource;
            
            let sourceHistory = state.intradayHistory?.[symbol] || [];
            if (inspectionSource === 'HistoricalSnapshot') sourceHistory = state.historicalHistory?.[symbol] || [];
            else if (inspectionSource === 'RangeIntraday' || inspectionSource === 'RangeEOD') sourceHistory = state.rangeHistory?.[symbol] || [];

            const historySnapshots = sourceHistory.slice();
            
            const fetchedLotSize = await fetchLotSizeFromCSV(symbol);
            
            if (historySnapshots.length === 0) {
                console.warn('No historical data available for backtest. Trying fullData fallback.');
                if (state.fullData[symbol]) {
                    historySnapshots.push(state.fullData[symbol]);
                }
            }

            const simulatedOrders: OMSOrder[] = [];
            let openOrders: OMSOrder[] = [];
            const pendingSignals: Record<string, { trade_type: string; first_seen: number }> = {};
            let lastProcessedTimestamp = Date.now();

            const globalConfig = configs['GLOBAL'] || {} as any;
            const isMasterActive = globalConfig.is_active !== 0;
            if (!isMasterActive) {
                console.warn('Engine is disabled globally.');
                return;
            }

            // ENFORCE GLOBAL SYNC: If Global config specifies a strike_range or base_type, force those so results match on all devices.
            const activeRange = globalConfig.strike_range || useSettingsStore.getState().range || 10;
            const settingsMode = globalConfig.base_type || useSettingsStore.getState().subTab || 'Combined OI';
            const gap = STRIKE_STEP[symbol as keyof typeof STRIKE_STEP] || 50;

            const baseSnapshot = find0916Snapshot(historySnapshots);

            const valKey = cellDataKeys[settingsMode] || 'oi';

            // Simple loop over all historical snapshots
            for (let i = 0; i < historySnapshots.length; i++) {
                if (i % 25 === 0 && i > 0) {
                    await new Promise(resolve => setTimeout(resolve, 0));
                }
                const snap = historySnapshots[i];
                const ts = snap.timestampIST || snap.timestamp || snap.datetime;
                let currentTimestamp = Date.now();
                if (typeof ts === 'number') currentTimestamp = ts;
                else if (typeof ts === 'string') {
                    let parsedTime = Date.now();
                    const isHistoricalMode = !useSettingsStore.getState().isLive;
                    const hDate = useSettingsStore.getState().historicalDate;
                    
                    if (ts.match(/^\d{2}-\d{2}-\d{4}/)) {
                        const [datePart, timePart] = ts.split(' ');
                        const [day, month, year] = (datePart || '').split('-');
                        parsedTime = new Date(`${year}-${month}-${day}T${timePart || '00:00:00'}+05:30`).getTime();
                    } else if (ts.match(/^\d{1,2}:\d{2}(:\d{2})?/)) {
                        const [h, m, s] = ts.split(':');
                        // Fix for different devices/timezones: ALWAYS evaluate as IST (+05:30)
                        let yyyy = new Date().getFullYear().toString();
                        let month = (new Date().getMonth() + 1).toString().padStart(2, '0');
                        let dd = new Date().getDate().toString().padStart(2, '0');
                        
                        if (isHistoricalMode && hDate) {
                            const parts = hDate.split('-');
                            yyyy = parts[0] || '2025';
                            month = parts[1] ? parts[1].padStart(2, '0') : '01';
                            dd = parts[2] ? parts[2].padStart(2, '0') : '01';
                        }
                        
                        const syntheticDate = new Date(`${yyyy}-${month}-${dd}T${(h || '0').padStart(2, '0')}:${(m || '0').padStart(2, '0')}:${(s || '0').padStart(2, '0')}+05:30`);
                        parsedTime = syntheticDate.getTime();
                    } else {
                        parsedTime = new Date(ts).getTime();
                    }
                    if (!isNaN(parsedTime) && parsedTime > 0) currentTimestamp = parsedTime;
                }
                lastProcessedTimestamp = currentTimestamp;

                const indexLtp = extractSpotPrice(snap, symbol);
                const dataWithSpot = { ...snap, spotPrice: indexLtp };
                const chain = extractSnapshotOptions(dataWithSpot, symbol, '');
                if (chain.length === 0) continue;

                const dateObj = new Date(currentTimestamp);
                const currentHHmm = `${dateObj.getHours().toString().padStart(2, '0')}:${dateObj.getMinutes().toString().padStart(2, '0')}`;

                // OPTIMIZATION: Cache values needed by exits loop
                let cachedFlatOpts: any[] | null = null;
                const getFlatOpts = () => {
                    if (!cachedFlatOpts) cachedFlatOpts = ensureFlatOpts(chain);
                    return cachedFlatOpts;
                };

                let atmStrikeForExit = Math.round(indexLtp / gap) * gap;
                let cachedSR: { supportStrike: number, resistanceStrike: number } | null = null;
                const getSR = () => {
                    if (!cachedSR) {
                        cachedSR = getSupportResistance(chain, atmStrikeForExit, activeRange, gap, settingsMode);
                    }
                    return cachedSR;
                };

                // 1. Check exits
                for (let i = openOrders.length - 1; i >= 0; i--) {
                    const order = openOrders[i] as OMSOrder;
                    if (!order) continue;
                    const specificConfig = configs[order.strategy_id] || ({} as any);
                    const globalConfig = configs['GLOBAL'] || {} as any;

                    const config: any = { ...specificConfig, strategy_id: order.strategy_id };
                    const globalKeysToApply = [
                        'option_mode', 'time_from', 'time_to', 'excluded_times', 
                        'stop_loss_type', 'stop_loss_value', 'stop_loss_trigger', 'trailing_sl_source', 'trailing_sl_type', 'trailing_sl_value',
                        'target_type', 'target_value', 'max_trades_per_day', 'max_trades_per_day_scope',
                        'charge_type', 'charge_amount', 'lots', 'allow_multiple_open', 'slippage'
                    ];
                    for (const key of globalKeysToApply) {
                        if ((globalConfig as any)[key] !== undefined && (globalConfig as any)[key] !== null) {
                            (config as any)[key] = (globalConfig as any)[key];
                        }
                    }
                    config.time_from = config.time_from || '09:15';
                    config.time_to = config.time_to || '15:30';

                    const flatOpts = getFlatOpts();
                    const strikeRow = flatOpts.find((r: any) => r.strike === order.strike && r.type === order.option_type);
                    let currentPremium = strikeRow ? (strikeRow.ltp || strikeRow.close || strikeRow.price || 0) : 0;
                    if (!currentPremium || currentPremium <= 0) currentPremium = order.entry_price;

                    const isSelling = config.option_mode === 'SELLING';

                    if (isSelling) {
                        if (!order.max_seen_premium || currentPremium < order.max_seen_premium) order.max_seen_premium = currentPremium;
                    } else {
                        if (currentPremium > (order.max_seen_premium || 0)) order.max_seen_premium = currentPremium;
                    }

                    let exitReason = null;
                    if (currentHHmm >= config.time_to) exitReason = 'Time Square Off';

                    let supportStrike = 0, resistanceStrike = 0;

                    if (!exitReason && (config.target_type === 'MIN_MAX' || config.stop_loss_trigger === 'MIN_MAX')) {
                        if (config.strategy_id === 'min_max_itm_top5') {
                            const minS = atmStrikeForExit - activeRange * gap;
                            const maxS = atmStrikeForExit + activeRange * gap;
                            const valKey = cellDataKeys[settingsMode] || 'oi';
                            // getConnectingDotsRows and getSortKey are imported statically
                            const { itmRows } = getConnectingDotsRows(dataWithSpot, symbol, expiry, atmStrikeForExit, activeRange, gap);
                            itmRows.sort((a: any, b: any) => getSortKey(b, settingsMode) - getSortKey(a, settingsMode));
                            const top5Itm = itmRows.slice(0, 5);
                            const validStrikes = top5Itm.map((r: any) => r.strike).filter((s: number) => s > 0);
                            if (validStrikes.length > 0) {
                                supportStrike = Math.min(...validStrikes);
                                resistanceStrike = Math.max(...validStrikes);
                            }
                        } else {
                            const { supportStrike: s, resistanceStrike: r } = getSR();
                            supportStrike = s;
                            resistanceStrike = r;
                        }
                    }

                    // Check Targets
                    if (!exitReason && config.target_type === 'PERCENTAGE' && config.target_value > 0) {
                        if (isSelling) {
                            const targetPrice = order.entry_price * (1 - (config.target_value / 100));
                            if (currentPremium <= targetPrice) exitReason = `Target Hit (${config.target_value}%)`;
                        } else {
                            const targetPrice = order.entry_price * (1 + (config.target_value / 100));
                            if (currentPremium >= targetPrice) exitReason = `Target Hit (${config.target_value}%)`;
                        }
                    }
                    if (!exitReason && config.target_type === 'PREMIUM' && config.target_value > 0) {
                        if (isSelling) {
                            if (currentPremium <= config.target_value) exitReason = `Target Premium (${config.target_value})`;
                        } else {
                            if (currentPremium >= config.target_value) exitReason = `Target Premium (${config.target_value})`;
                        }
                    }
                    if (!exitReason && config.target_type === 'MIN_MAX') {
                        if (config.strategy_id === 'min_max_itm_top5') {
                            if (order.option_type === 'CE' && atmStrikeForExit >= resistanceStrike && resistanceStrike > 0) exitReason = 'Target Hit (MAX)';
                            if (order.option_type === 'PE' && atmStrikeForExit <= supportStrike && supportStrike > 0) exitReason = 'Target Hit (MIN)';
                        } else {
                            if (order.option_type === 'CE' && indexLtp >= resistanceStrike && resistanceStrike > 0) exitReason = 'Target Hit (Resistance)';
                            if (order.option_type === 'PE' && indexLtp <= supportStrike && supportStrike > 0) exitReason = 'Target Hit (Support)';
                        }
                    }

                    // Check Stop Loss
                    if (!exitReason && config.stop_loss_type === 'FIXED') {
                        if (config.stop_loss_trigger === 'WAIT' && order.reversal_time) {
                            if (currentTimestamp - order.reversal_time >= (config.stop_loss_wait_mins * 60000)) {
                                exitReason = 'Stop Loss (Reversal Wait)';
                            }
                        }
                        if (config.stop_loss_trigger === 'MIN_MAX') {
                            if (config.strategy_id === 'min_max_itm_top5') {
                                if (order.option_type === 'CE' && atmStrikeForExit < supportStrike && supportStrike > 0) exitReason = 'Stop Loss Hit (MIN)';
                                if (order.option_type === 'PE' && atmStrikeForExit > resistanceStrike && resistanceStrike > 0) exitReason = 'Stop Loss Hit (MAX)';
                            } else {
                                if (order.option_type === 'CE' && indexLtp <= supportStrike && supportStrike > 0) exitReason = 'Stop Loss Hit (Support)';
                                if (order.option_type === 'PE' && indexLtp >= resistanceStrike && resistanceStrike > 0) exitReason = 'Stop Loss Hit (Resistance)';
                            }
                        }
                    }

                    if (!exitReason && config.stop_loss_type === 'TRAILING' && order.max_seen_premium) {
                        if (config.trailing_sl_source === 'PREMIUM') {
                            if (config.trailing_sl_type === 'POINTS') {
                                if (isSelling) {
                                    if (currentPremium >= order.max_seen_premium + config.trailing_sl_value) exitReason = 'Trailing SL Hit (Points)';
                                } else {
                                    if (currentPremium <= order.max_seen_premium - config.trailing_sl_value) exitReason = 'Trailing SL Hit (Points)';
                                }
                            } else if (config.trailing_sl_type === 'PERCENTAGE') {
                                if (isSelling) {
                                    if (currentPremium >= order.max_seen_premium * (1 + config.trailing_sl_value / 100)) exitReason = 'Trailing SL Hit (%)';
                                } else {
                                    if (currentPremium <= order.max_seen_premium * (1 - config.trailing_sl_value / 100)) exitReason = 'Trailing SL Hit (%)';
                                }
                            }
                        }
                    }

                    if (exitReason) {
                        order.status = 'CLOSED';
                        order.exit_time = currentTimestamp;
                        order.exit_price = currentPremium;
                        order.exit_remarks = exitReason;
                        if (isSelling) {
                            order.order_pl = (order.entry_price - (order.exit_price || order.entry_price)) * order.qty;
                        } else {
                            order.order_pl = ((order.exit_price || order.entry_price) - order.entry_price) * order.qty;
                        }
                        order.net_pl = (order.order_pl || 0) - order.charges;
                        openOrders.splice(i, 1);
                    }
                }

                // 2. Check entries
                // OPTIMIZATION: Compute Trend Data ONCE per snapshot, not once per strategy!
                let tickTrendData: any = null;
                const getTickTrendData = () => {
                    if (!tickTrendData) {
                        const currentHistorySlice = historySnapshots.slice(0, i + 1);
                        
                        // Use buildProcessedHistory to ensure robust time-based sorting (newest first), exactly like multiday-backtest.worker.ts
                        const timeRangeEnd = extractTimePart(ts);
                        
                        const sortedHistorySlice = buildProcessedHistory(currentHistorySlice, timeRangeEnd, false);
                        tickTrendData = calculateTrendAnalysis(sortedHistorySlice, symbol, expiry, gap, activeRange, valKey);
                    }
                    return tickTrendData;
                };

                Object.entries(strategyEvaluators).forEach(([strategyId, evaluator]) => {
                    if (!evaluator) return;

                    const specificConfig = configs[strategyId] || {};
                    const globalConfig = configs['GLOBAL'] || {} as any;
                    
                    const config: any = { ...specificConfig, strategy_id: strategyId };

                    // Explicitly apply global settings without blindly overwriting is_active, name, etc.
                    const globalKeysToApply = [
                        'option_mode', 'time_from', 'time_to', 'excluded_times', 'entry_type', 'entry_wait_mins', 'reversal_cooldown_mins',
                        'stop_loss_type', 'stop_loss_value', 'stop_loss_trigger', 'trailing_sl_source', 'trailing_sl_type', 'trailing_sl_value',
                        'target_type', 'target_value', 'max_trades_per_day', 'max_trades_per_day_scope',
                        'charge_type', 'charge_amount', 'lots', 'allow_multiple_open', 'slippage'
                    ];

                    for (const key of globalKeysToApply) {
                        if ((globalConfig as any)[key] !== undefined && (globalConfig as any)[key] !== null) {
                            (config as any)[key] = (globalConfig as any)[key];
                        }
                    }

                    config.time_from = config.time_from || '09:15';
                    config.time_to = config.time_to || '15:30';
                    
                    if (config.is_active === 0) return;
                    if (currentHHmm < config.time_from || currentHHmm >= config.time_to) return;

                    if (config.excluded_times) {
                        try {
                            const excluded = JSON.parse(config.excluded_times);
                            const isExcluded = excluded.some((et: any) => currentHHmm >= et.from && currentHHmm <= et.to);
                            if (isExcluded) return;
                        } catch (e) { console.warn('Ignored error in OMSProvider.tsx:', e); }
                    }
                    
                    const context: StrategyContext = {
                        symbol,
                        expiry,
                        baseSnapshot,
                        settingsMode,
                        activeRange,
                        gap,
                        chain,
                        liveIndicators: {
                            trendAnalysisData: getTickTrendData()
                        }
                    };

                    const rawSignal = evaluator(dataWithSpot, currentTimestamp, config, context);
                    
                    if (!rawSignal) {
                        delete pendingSignals[strategyId];
                    }

                    const signalsToProcess = rawSignal ? (Array.isArray(rawSignal) ? rawSignal : [rawSignal]) : [];
                    const primarySignal = signalsToProcess[0];

                    // ── REVERSAL / SIGNAL DISAPPEARANCE CHECK ──
                    for (let k = openOrders.length - 1; k >= 0; k--) {
                        const order = openOrders[k] as OMSOrder;
                        if (!order || order.strategy_id !== strategyId) continue;

                        const signalMatchesOrder = signalsToProcess.some(s => s.trade_type === order.trade_type && s.strike === order.strike && s.option_type === order.option_type);

                        if (signalMatchesOrder) {
                            // Signal still agrees → clear any pending reversal timer
                            if (order.reversal_time !== null) order.reversal_time = null;
                            continue;
                        }

                        // Signal disagrees or disappeared
                        const isSignalDisappeared = signalsToProcess.length === 0; // Went Sideways (null)
                        const isActualReversal = !isSignalDisappeared && !signalMatchesOrder;

                        const flatOptsRev = ensureFlatOpts(chain);
                        const strikeRowFlat = flatOptsRev.find((r: any) => r.strike === order.strike && r.type === order.option_type);
                        let currentPremium = strikeRowFlat ? (strikeRowFlat.ltp || strikeRowFlat.close || strikeRowFlat.price || 0) : 0;
                        if (!currentPremium || currentPremium <= 0) currentPremium = order.entry_price;

                        if (isSignalDisappeared) {
                            // Signal went to Sideways → EXIT immediately
                            order.status = 'CLOSED';
                            order.exit_time = currentTimestamp;
                            order.exit_price = currentPremium;
                            order.exit_remarks = 'Signal Lost (Sideways)';
                            if (config.option_mode === 'SELLING') {
                                order.order_pl = (order.entry_price - currentPremium) * order.qty;
                            } else {
                                order.order_pl = (currentPremium - order.entry_price) * order.qty;
                            }
                            order.net_pl = (order.order_pl || 0) - order.charges;
                            openOrders.splice(k, 1);
                        } else if (isActualReversal) {
                            // Opposite signal appeared
                            const useWaitLogic = config.stop_loss_type === 'FIXED'
                                && config.stop_loss_trigger === 'WAIT'
                                && config.target_type !== 'REVERSAL';

                            if (useWaitLogic) {
                                // Start reversal wait timer (don't exit yet)
                                if (!order.reversal_time) order.reversal_time = currentTimestamp;
                            } else {
                                // Exit immediately on reversal
                                order.status = 'CLOSED';
                                order.exit_time = currentTimestamp;
                                order.exit_price = currentPremium;
                                order.exit_remarks = 'Reversal Exit';
                                const isSelling = config.option_mode === 'SELLING';
                                if (isSelling) {
                                    order.order_pl = (order.entry_price - currentPremium) * order.qty;
                                } else {
                                    order.order_pl = (currentPremium - order.entry_price) * order.qty;
                                }
                                order.net_pl = (order.order_pl || 0) - order.charges;
                                openOrders.splice(k, 1);
                            }
                        }
                    }

                    if (signalsToProcess.length > 0) {
                        // Filter out signals that we already have an open order for
                        const newSignals = signalsToProcess.filter(s => {
                            return !openOrders.some(o => o.strategy_id === strategyId && o.trade_type === s.trade_type && o.strike === s.strike && o.option_type === s.option_type);
                        });

                        if (newSignals.length === 0) return; // All legs are already open

                        const hasStrategyOpenOrder = openOrders.some(o => o.strategy_id === strategyId);
                        const hasAnyOpenOrder = openOrders.length > 0;
                        
                        if (Number(config.allow_multiple_open || 0) === 0 && hasStrategyOpenOrder) return;
                        if (Number(config.allow_multiple_open || 0) === 2 && hasAnyOpenOrder) return;

                        // ── MAX TRADES PER DAY ENFORCEMENT ──
                        const todayStart = new Date(currentTimestamp);
                        todayStart.setHours(0, 0, 0, 0);
                        const todayStartTs = todayStart.getTime();

                        const maxTrades = Number(config.max_trades_per_day || 0);
                        const maxTradesScope = Number(config.max_trades_per_day_scope || 0);

                        if (maxTrades > 0) {
                            if (maxTradesScope === 1) {
                                const globalTodayCount = simulatedOrders.filter(o => o.entry_time >= todayStartTs).length;
                                if (globalTodayCount >= maxTrades) return;
                            } else {
                                const stratTodayCount = simulatedOrders.filter(o => o.strategy_id === strategyId && o.entry_time >= todayStartTs).length;
                                if (stratTodayCount >= maxTrades) return;
                            }
                        }

                        // Consecutive Signal Confirmation Logic
                        if (config.entry_type === 'WAIT' && config.entry_wait_mins > 0 && primarySignal) {
                            const pending = pendingSignals[strategyId];
                            if (!pending || pending.trade_type !== primarySignal.trade_type) {
                                // First time seeing this signal, start the timer
                                pendingSignals[strategyId] = { trade_type: primarySignal.trade_type, first_seen: currentTimestamp };
                                return; // Do not enter yet
                            } else {
                                // We have seen it before. Check if enough time has passed.
                                const minsSinceFirstSeen = (currentTimestamp - pending.first_seen) / 60000;
                                if (minsSinceFirstSeen < config.entry_wait_mins) {
                                    return; // Still waiting
                                }
                            }
                        }
                        
                        // Clear pending since we are going to enter
                        delete pendingSignals[strategyId];

                        // Reversal Cooldown Logic
                        if (config.reversal_cooldown_mins > 0) {
                            const lastClosed = simulatedOrders.slice().reverse().find(o => o.strategy_id === strategyId && o.status === 'CLOSED');
                            if (lastClosed && lastClosed.exit_time) {
                                const minsSinceExit = (currentTimestamp - lastClosed.exit_time) / 60000;
                                if (minsSinceExit < config.reversal_cooldown_mins) {
                                    return; // Wait for cooldown
                                }
                            }
                        }

                        const symbolLotFallback = (symbol || '').includes("NIFTY") ? 75 : ((symbol || '').includes("SENSEX") ? 10 : ((symbol || '').includes("BANK") ? 15 : 65));
                        
                        // Use the fetched lot size from outside the loop
                        const effectiveLotSize = (useSettingsStore.getState() as any).lotSize || fetchedLotSize || symbolLotFallback;

                        newSignals.forEach(signal => {
                            const newOrder: OMSOrder = {
                                id: `oms-${Date.now()}-${Math.floor(Math.random() * 1000)}`,
                                strategy_id: config.strategy_id,
                                symbol: symbol || 'NIFTY',
                                trade_type: signal.trade_type,
                                strike: signal.strike,
                                option_type: signal.option_type,
                                status: 'OPEN',
                                entry_time: currentTimestamp,
                                entry_price: signal.entry_price,
                                qty: Math.max((config.lots || 1), 1) * effectiveLotSize,
                                capital_used: signal.entry_price * (Math.max((config.lots || 1), 1) * effectiveLotSize),
                                entry_remarks: signal.remarks,
                                exit_time: null,
                                exit_price: null,
                                exit_remarks: null,
                                order_pl: null,
                                charges: config.charge_type === 'PER_LOT' ? (config.charge_amount || 50) * Math.max((config.lots || 1), 1) : (config.charge_amount ?? 50),
                                net_pl: null,
                                max_seen_premium: signal.entry_price,
                                reversal_time: null
                            };
                            simulatedOrders.push(newOrder);
                            openOrders.push(newOrder);
                        });
                    }
                });
            }

            // Close any remaining open orders at the end of the day
            openOrders.forEach(order => {
                order.status = 'CLOSED';
                order.exit_time = lastProcessedTimestamp;
                
                let finalExitPrice = order.max_seen_premium || order.entry_price;
                if (historySnapshots.length > 0) {
                    const lastSnap = historySnapshots[historySnapshots.length - 1];
                    const lastChain = extractSnapshotOptions(lastSnap, symbol, '');
                    const lastFlatOpts = ensureFlatOpts(lastChain);
                    const strikeRow = lastFlatOpts.find((r: any) => r.strike === order.strike && r.type === order.option_type);
                    if (strikeRow) {
                        finalExitPrice = strikeRow.ltp || strikeRow.close || strikeRow.price || order.entry_price;
                    }
                }
                order.exit_price = finalExitPrice;
                
                order.exit_remarks = 'Time Square Off (EOD)';
                const isSellingOrder = configs[order.strategy_id]?.option_mode === 'SELLING' || configs['GLOBAL']?.option_mode === 'SELLING';
                if (isSellingOrder) {
                    order.order_pl = (order.entry_price - (order.exit_price || order.entry_price)) * order.qty;
                } else {
                    order.order_pl = ((order.exit_price || order.entry_price) - order.entry_price) * order.qty;
                }
                order.net_pl = (order.order_pl || 0) - order.charges;
            });

            // Put them into React state
            setOrders(simulatedOrders.reverse());
        } catch (error) {
            console.error('Backtest request failed:', error);
        } finally {
            setIsLoading(false);
        }
    }, [configs]);

    return (
        <OMSContext.Provider value={{
            configs, orders, isLoading, updateConfig, addOrder, updateOrder, addOrderLocal, updateOrderLocal, runBacktest, refreshData: loadData
        }}>
            {children}
        </OMSContext.Provider>
    );
};

export const useOMS = () => {
    const context = useContext(OMSContext);
    if (context === null) {
        // Fallback for Vite HMR context-splitting during dev
        console.warn('useOMS used outside OMSProvider (or HMR split the context). Returning fallback.');
        return {
            configs: {}, orders: [], isLoading: false,
            updateConfig: async () => {}, addOrder: async () => {}, updateOrder: async () => {},
            addOrderLocal: () => {}, updateOrderLocal: () => {}, runBacktest: async () => {}, refreshData: async () => {}
        } as OMSContextType;
    }
    return context;
};
